import dayjs from "dayjs";
import {
  WecomAudioMessageType,
  WecomBusinessCardMessageType,
  WecomFileMessageType,
  WecomLinkMessageType,
  WecomLocationMessageType,
  WecomMessageType,
  WecomMiniProgramMessageType,
  WecomMsgType,
  WecomSystemMessageType,
  WecomTextMessageType,
  WecomVideoNumMessageType,
} from "../types/wecom";
import {
  ChannelTypeEnum,
  MailDataInfo,
  WechatAudioMessageType,
  WechatBusinessCardMessageType,
  WechatGhBusinessCardMessageType,
  WechatLinkMessageType,
  WechatLocationMessageType,
  WechatMessageType,
  WechatMiniProgramMessageType,
  WechatMsgType,
  WechatRoomInviteLinkMessageType,
  WechatSystemMessageType,
  WechatTextMessageType,
  WechatVideoNumMessageType,
  WechatWecomBusinessCardMessageType,
  WecomRoomMember,
} from "../types";

export const getWsBasePath = () => {
  if (process.env.NODE_ENV === "development") {
    return "wss://qasplatform.12times.com";
  }
  return `wss://${window.location.host}`;
};

// 格式化最近聊天列表的时间
const days = ["日", "一", "二", "三", "四", "五", "六"];
export const formatChatTime = (last_modify_time: string) => {
  const now = new Date();
  const chatTime = new Date(last_modify_time);
  // 计算时间差（单位：毫秒）
  const diff = now.getTime() - chatTime.getTime();
  // 小于1分钟
  if (diff < 60000) {
    return "刚刚";
  }
  // 小于1小时
  else if (diff < 3600000) {
    const minutes = Math.floor(diff / 60000);
    return `${minutes}分钟前`;
  }
  // 小于24小时
  else if (diff < 86400000) {
    return dayjs(chatTime).format("HH:mm");
  }
  // 大于24小时
  else if (diff < 172800000) {
    return "昨天";
  }
  // 大于48小时
  else if (diff < 3 * 24 * 60 * 60 * 1000) {
    return "前天";
  }
  // 大于72小时
  else if (diff < 7 * 24 * 60 * 60 * 1000) {
    return `星期${days[dayjs(chatTime).get("day")]}`;
  } else if (diff < 365 * 24 * 60 * 60 * 1000) {
    return dayjs(chatTime).format("M/D");
  }
  // 其他情况
  else {
    return dayjs(chatTime).format("YYYY/M/D");
  }
};

// 处理最近聊天信息里面的最后一条信息
export const dealLastMessage = (
  channelType: ChannelTypeEnum,
  data?: WecomMessageType | WechatMessageType | MailDataInfo
) => {
  if (!data) {
    return "";
  }
  if (channelType === ChannelTypeEnum.Wechat) {
    const msgType = (data as WechatMessageType)?.msg_type;
    switch (msgType) {
      case WechatMsgType.TEXT:
      case WechatMsgType.TEXT_2: {
        const msg = data as WechatTextMessageType;
        return msg?.content;
      }
      case WechatMsgType.BUSINESS_CARD: {
        const msg = data as WechatBusinessCardMessageType;
        return `[个人名片] ${msg.nickname}`;
      }
      case WechatMsgType.GH_BUSINESS_CARD: {
        const msg = data as WechatGhBusinessCardMessageType;
        return `[公众号名片] ${msg.nickname}`;
      }
      case WechatMsgType.WECOM_BUSINESS_CARD: {
        const msg = data as WechatWecomBusinessCardMessageType;
        return `[企业微信名片] ${msg.nickname}`;
      }
      case WechatMsgType.LINK: {
        const msg = data as WechatLinkMessageType;
        return `[链接] ${msg.title}`;
      }
      case WechatMsgType.ROOM_INVITE_LINK: {
        const msg = data as WechatRoomInviteLinkMessageType;
        return `[群邀请链接] ${msg.title}`;
      }
      case WechatMsgType.HONGBAO: {
        return `[收到一个红包，请在手机上查看]`;
      }
      case WechatMsgType.AUDIO: {
        const msg = data as WechatAudioMessageType;
        return `[语音] ${msg.duration !== undefined ? `${msg.duration}"` : ""}`;
      }
      case WechatMsgType.WECHAT_CALL: {
        return `[语音通话]`;
      }
      case WechatMsgType.WECHAT_IMAGE:
      case WechatMsgType.WECOM_IMAGE: {
        return "[图片]";
      }
      case WechatMsgType.GIF:
      case WechatMsgType.GIF_104: {
        return "[动画表情]";
      }
      case WechatMsgType.LOCATION: {
        const msg = data as WechatLocationMessageType;
        return `[位置] ${msg?.name}`;
      }
      case WechatMsgType.BIG_FILE:
      case WechatMsgType.WECOM_FILE:
      case WechatMsgType.WECHAT_FILE: {
        const msg = data as WecomFileMessageType;
        return `[文件] ${msg.file_name}`;
      }
      case WechatMsgType.WECHAT_VIDEO:
      case WechatMsgType.BIG_VIDEO:
      case WechatMsgType.WECOM_VIDEO: {
        return `[视频]`;
      }
      case WechatMsgType.VIDEO_NUM: {
        const msg = data as WechatVideoNumMessageType;
        return `[视频号]${msg.username}的视频`;
      }
      case WechatMsgType.MINI_PROGRAM: {
        const msg = data as WechatMiniProgramMessageType;
        return `[小程序] ${msg.desc || ""}`;
      }
      default:
        if (msgType >= WechatMsgType.SYSTEM_NOTICE) {
          const msg = data as WechatSystemMessageType;
          return msg.content;
        }
        return "";
    }
  } else if (channelType === ChannelTypeEnum.Wecom) {
    const msgType = (data as WecomMessageType)?.msgtype;
    switch (msgType) {
      case WecomMsgType.TEXT:
      case WecomMsgType.TEXT_2: {
        const msg = data as WecomTextMessageType;
        return msg?.content;
      }
      case WecomMsgType.CHAT_RECORD: {
        return `[聊天记录]`;
      }
      case WecomMsgType.BUSINESS_CARD: {
        const msg = data as WecomBusinessCardMessageType;
        return `[个人名片] ${msg.nickname}`;
      }
      case WecomMsgType.LINK: {
        const msg = data as WecomLinkMessageType;
        return `[链接] ${msg.title}`;
      }
      case WecomMsgType.HONGBAO: {
        return `[收到一个红包，请在手机上查看]`;
      }
      case WecomMsgType.AUDIO: {
        const msg = data as WecomAudioMessageType;
        return `[语音] ${msg.duration !== undefined ? `${msg.duration}"` : ""}`;
      }
      case WecomMsgType.WECHAT_CALL: {
        return `[语音通话]`;
      }
      case WecomMsgType.WECHAT_IMAGE:
      case WecomMsgType.WECOM_IMAGE: {
        return "[图片]";
      }
      case WecomMsgType.GIF:
      case WecomMsgType.GIF_104: {
        return "[动画表情]";
      }
      case WecomMsgType.LOCATION: {
        const msg = data as WecomLocationMessageType;
        return `[位置] ${msg?.name}`;
      }
      case WecomMsgType.BIG_FILE:
      case WecomMsgType.WECOM_FILE:
      case WecomMsgType.WECHAT_FILE: {
        const msg = data as WecomFileMessageType;
        return `[文件] ${msg.file_name || ""}`;
      }
      case WecomMsgType.WECHAT_VIDEO:
      case WecomMsgType.BIG_VIDEO:
      case WecomMsgType.WECOM_VIDEO: {
        return `[视频]`;
      }
      case WecomMsgType.VIDEO_NUM: {
        const msg = data as WecomVideoNumMessageType;
        return `[视频号]${msg.username}的视频`;
      }
      case WecomMsgType.MINI_PROGRAM: {
        const msg = data as WecomMiniProgramMessageType;
        return `[小程序] ${msg.desc || ""}`;
      }
      default:
        if (msgType >= WecomMsgType.SYSTEM_NOTICE) {
          const msg = data as WecomSystemMessageType;
          return msg.content;
        }
        return "";
    }
  }
};

export const sortMemberList = (
  member_list: WecomRoomMember[],
  room_owner_id: string
) => {
  const list = [...member_list];
  list?.sort((_a, b) => (b.user_id === room_owner_id ? 1 : -1)) || [];
  return list;
};

export const sortConversationList = <
  T extends { chat_id: string; last_message?: { timestamp: number } }
>(
  conversationsMap: Record<string, T>
) => {
  const conversations = Object.values(conversationsMap);
  const sortConversations = conversations.sort((a, b) => {
    if (
      (!a?.last_message && b?.last_message) ||
      (a?.last_message &&
        b?.last_message &&
        a?.last_message?.timestamp - b?.last_message?.timestamp < 0)
    )
      return 1;
    return -1;
  });
  const finalConversations = {} as Record<string, T>;
  sortConversations.forEach((c) => {
    finalConversations[c.chat_id] = c;
  });
  return finalConversations;
};

/**
 * 从 html 获取纯文本
 * @param html
 */
export const getPureTextFromHtml = (html?: string) => {
  if (!html) return "";

  try {
    const dom = document.createElement("div");
    dom.innerHTML = html;
    ["head", "script", "style"].forEach((tag) =>
      dom
        .querySelectorAll(tag as keyof HTMLElementTagNameMap)
        ?.forEach((d) => d.remove())
    );
    return dom.textContent?.trim() || "";
  } catch {
    return "";
  }
};

/**
 * 判定编辑器是否为空
 * @param html
 * @returns
 */
export const isEditorEmpty = (html?: string) => {
  if (!html) return true;

  const dom = document.createElement("div");
  dom.innerHTML = html;

  return (
    // 文本内容不存在，且不存在特殊标签
    dom.textContent?.trim() === "" &&
    !["img", "video"].some((t) => !!dom.querySelector(t))
  );
};
